/**
 * Workbook Brain Main Component
 * Interface unifiée pour toutes les analyses et capacités IA du classeur
 */

import React, { useState, useEffect } from 'react';
import { WorkbookBrainOverview } from '../features/WorkbookBrainOverview';
import { DependencyGraphPanel } from '../features/DependencyGraphPanel';
import { WorkbookRiskPanel } from '../features/WorkbookRiskPanel';
import { WorkbookPerformancePanel } from '../features/WorkbookPerformancePanel';
import { WorkbookDocumentationPanel } from '../features/WorkbookDocumentationPanel';
import { WorkbookOptimizationPanel } from '../features/WorkbookOptimizationPanel';
import { ChangesVisualizerPanel, type Change } from '../features/ChangesVisualizerPanel';
import { WorkbookBrainChat } from '../features/WorkbookBrainChat';
import { Header } from '../common/Header';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { implementOptimization } from '../../services/optimization-executor.service';
import { printDiagnosticReport } from '../../services/excel-diagnostics.service';
import type { OptimizationPriority } from '@excel-ai-studio/shared';

type TabType =
  | 'overview'
  | 'dependencies'
  | 'risks'
  | 'performance'
  | 'documentation'
  | 'optimization'
  | 'changes'
  | 'chat';

/**
 * Composant principal Workbook Brain
 */
export const WorkbookBrainMain: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isInitializing, setIsInitializing] = useState(true);
  const [implementationStatus, setImplementationStatus] = useState<{
    isRunning: boolean;
    message: string;
    error?: string;
  }>({ isRunning: false, message: '' });
  const [changes, setChanges] = useState<Change[]>([]);
  const [showChangesPanel, setShowChangesPanel] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitializing(false);
      // Exécuter le diagnostic au démarrage
      console.log('🚀 Workbook Brain initialized - Running diagnostics...');
      printDiagnosticReport().catch(console.error);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  const handleImplement = async (priority: OptimizationPriority) => {
    console.log('✅ handleImplement appelé avec:', priority.title);
    setImplementationStatus({ isRunning: true, message: `Implémentation de: ${priority.title}...` });

    try {
      const result = await implementOptimization(priority);

      // Convertir les changements au format du visualiseur
      const visualChanges: Change[] = (result.changes || []).map((change: any) => ({
        sheet: change.sheet,
        cell: change.cell,
        before: change.before,
        after: change.after,
        type: 'formula' as const,
        status: 'applied' as const,
      }));

      // Ajouter les changements à la liste
      setChanges((prev) => [...prev, ...visualChanges]);
      setShowChangesPanel(true);

      // Afficher les détails des modifications
      let displayMessage = `✅ ${result.message}`;
      if (result.details) {
        displayMessage += `\n\n${result.details}`;
      }

      setImplementationStatus({
        isRunning: false,
        message: displayMessage,
      });

      // Log les changements dans la console
      if (result.changes && result.changes.length > 0) {
        console.log('📋 Détails des modifications:');
        result.changes.forEach((change: any) => {
          console.log(`  ${change.sheet}!${change.cell}:`);
          console.log(`    Avant: ${change.before}`);
          console.log(`    Après: ${change.after}`);
        });
      }

      // Réinitialiser le message après 10 secondes (plus long pour lire les détails)
      setTimeout(() => {
        setImplementationStatus({ isRunning: false, message: '' });
      }, 10000);
    } catch (error) {
      console.error('❌ Erreur d\'implémentation:', error);
      setImplementationStatus({
        isRunning: false,
        message: '',
        error: error instanceof Error ? error.message : 'Erreur inconnue',
      });

      // Réinitialiser le message d'erreur après 5 secondes
      setTimeout(() => {
        setImplementationStatus({ isRunning: false, message: '' });
      }, 5000);
    }
  };

  if (isInitializing) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <LoadingSpinner label="Initialisation du Cerveau Classeur..." />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Header />

      {/* Navigation par onglets */}
      <div
        style={{
          display: 'flex',
          borderBottom: '2px solid #ddd',
          backgroundColor: '#f5f5f5',
          padding: '0 16px',
          overflowX: 'auto',
        }}
      >
        {[
          { id: 'overview', label: '🧠 Cerveau' },
          { id: 'dependencies', label: '🔗 Dépendances' },
          { id: 'risks', label: '⚠️ Risques' },
          { id: 'performance', label: '⚡ Performance' },
          { id: 'documentation', label: '📝 Docs' },
          { id: 'optimization', label: '🚀 Optimiser' },
          { id: 'changes', label: `📊 Changements${changes.length > 0 ? ` (${changes.length})` : ''}` },
          { id: 'chat', label: '💬 Chat' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as TabType)}
            style={{
              padding: '12px 16px',
              backgroundColor: activeTab === tab.id ? 'white' : 'transparent',
              border: 'none',
              borderBottom: activeTab === tab.id ? '3px solid #2196F3' : 'none',
              cursor: 'pointer',
              fontWeight: activeTab === tab.id ? 'bold' : 'normal',
              color: activeTab === tab.id ? '#2196F3' : '#666',
              fontSize: '13px',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu des onglets */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        {activeTab === 'overview' && <WorkbookBrainOverview />}

        {activeTab === 'dependencies' && (
          <DependencyGraphPanel
            onAnalysisComplete={(graph) => {
              console.log('Analyse des dépendances terminée :', graph);
            }}
          />
        )}

        {activeTab === 'risks' && (
          <WorkbookRiskPanel
            onAnalysisComplete={(report) => {
              console.log('Analyse des risques terminée :', report);
            }}
          />
        )}

        {activeTab === 'performance' && (
          <WorkbookPerformancePanel
            onAnalysisComplete={(report) => {
              console.log('Analyse des performances terminée :', report);
            }}
          />
        )}

        {activeTab === 'documentation' && (
          <WorkbookDocumentationPanel
            onExport={(format) => {
              console.log('Exporter la documentation en :', format);
            }}
          />
        )}

        {activeTab === 'optimization' && (
          <div>
            {implementationStatus.message && (
              <div
                style={{
                  padding: '12px',
                  marginBottom: '12px',
                  backgroundColor: implementationStatus.error ? '#ffebee' : '#e8f5e9',
                  color: implementationStatus.error ? '#c62828' : '#2e7d32',
                  borderRadius: '4px',
                  fontSize: '13px',
                  border: `1px solid ${implementationStatus.error ? '#ef5350' : '#4CAF50'}`,
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                }}
              >
                {implementationStatus.error ? '❌' : '✅'} {implementationStatus.message}
              </div>
            )}
            <WorkbookOptimizationPanel onImplement={handleImplement} />
          </div>
        )}

        {activeTab === 'changes' && (
          <ChangesVisualizerPanel
            changes={changes}
            title="📊 Historique des Changements"
            onApplyAll={() => {
              console.log('Appliquer tous les changements');
            }}
            onRejectAll={() => {
              setChanges([]);
              setShowChangesPanel(false);
            }}
          />
        )}

        {activeTab === 'chat' && (
          <WorkbookBrainChat
            onMessageSent={(message) => {
              console.log('Message envoyé :', message);
            }}
          />
        )}
      </div>

      {/* Pied de page */}
      <div
        style={{
          padding: '12px 16px',
          backgroundColor: '#f5f5f5',
          borderTop: '1px solid #ddd',
          fontSize: '11px',
          color: '#999',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>🧠 Cerveau Classeur - Analyse IA du Classeur</div>
        <div>
          <span style={{ marginRight: '16px' }}>📊 Phase 3 Complète</span>
          <span>✅ Toutes les fonctionnalités actives</span>
        </div>
      </div>
    </div>
  );
};
